<div class="backdrop-blur-sm">
    <div class="flex items-center justify-between">
        <div class="flex flex-col gap-0.5">
            <h1 class="text-2xl font-bold">Chatbot Settings & Configuration</h1>
            <p class="text-xs text-gray-500 dark:text-gray-400">Configure your chatbot's settings and manage LLM models
            </p>
        </div>
        <div class="flex items-center justify-end gap-2">
            <button type="button" (click)="setTab('default_prompt')"
                [class.bg-purple-500]="selectedTab() === 'default_prompt'"
                [class.text-purple-50]="selectedTab() === 'default_prompt'"
                [class.text-purple-500]="selectedTab() !== 'default_prompt'"
                [class.hover:text-purple-600]="selectedTab() !== 'default_prompt'"
                class="text-sm tracking-wider py-2 px-4 rounded-lg transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900">
                Default Prompts
            </button>
            <button type="button" (click)="setTab('ai_providers')"
                [class.bg-purple-500]="selectedTab() === 'ai_providers'"
                [class.text-purple-50]="selectedTab() === 'ai_providers'"
                [class.text-purple-500]="selectedTab() !== 'ai_providers'"
                [class.hover:text-purple-600]="selectedTab() !== 'ai_providers'"
                class="text-sm tracking-wider py-2 px-4 rounded-lg transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900">
                AI Providers
            </button>
        </div>
    </div>

    @switch (selectedTab()) {
    @case ('default_settings') {
    <div class="mt-6 p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white/50 dark:bg-gray-800/50">
        <h2 class="text-lg font-semibold mb-2">Default Chatbot Create Settings</h2>
        <p class="text-sm text-gray-600 dark:text-gray-400">Configure default options when creating new chatbots.</p>
    </div>
    }
    @case ('default_prompt') {
    <div class="mt-6 space-y-4">
        <div
            class="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/80 shadow-sm overflow-hidden card-classes">
            <div class="px-5 py-4 border-b border-gray-100 dark:border-gray-700/80">
                <h2 class="text-base font-semibold text-gray-900 dark:text-white">Default Prompts</h2>
                <p class="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Manage system prompts for your chatbots.</p>
            </div>
            <div class="p-5 space-y-4">
                <div
                    class="rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-700/20 p-4 space-y-3">
                    <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Add prompt</span>
                    <div class="space-y-3">
                        <div>
                            <label class="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Label *</label>
                            <input type="text" [ngModel]="newPromptLabel()" (ngModelChange)="newPromptLabel.set($event)"
                                (blur)="markPromptTouched('label')"
                                placeholder="e.g. customer-support, sales-assistant" maxlength="255"
                                [class.border-red-500]="promptFormTouched()['label'] && promptFormErrors().label"
                                class="w-full max-w-md rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700/50 text-gray-900 dark:text-gray-100 text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-colors" />
                            @if (promptFormTouched()['label'] && promptFormErrors().label) {
                            <p class="text-[10px] text-red-500 mt-1 font-medium">{{ promptFormErrors().label }}</p>
                            }
                        </div>
                        <div>
                            <label class="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Prompt
                                *</label>
                            <app-textarea [ngModel]="newPromptText()" (ngModelChange)="newPromptText.set($event)"
                                (inputBlur)="markPromptTouched('prompt')"
                                placeholder="Enter the system prompt text..." [rows]="4"
                                [error]="promptFormTouched()['prompt'] ? (promptFormErrors().prompt || '') : ''" />
                        </div>
                        <button type="button" (click)="addPromptFromForm()"
                            class="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-purple-500 hover:bg-purple-600 disabled:opacity-50 disabled:pointer-events-none text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 transition-colors">
                            <app-icon-v2 name="plus" [size]="14" />
                            Add
                        </button>
                    </div>
                </div>

                @if (promptsLoading()) {
                <div class="flex items-center gap-2 py-4 text-sm text-gray-500 dark:text-gray-400">
                    <app-icon-v2 name="LoaderCircle" [size]="16" customClass="animate-spin" />
                    <span>Loading prompts…</span>
                </div>
                } @else {
                <div class="my-4">
                    <h3 class="text-sm font-medium text-gray-700 dark:text-gray-300">Active Prompts: {{
                        activePrompts().length || 0 }}</h3>
                </div>

                <div class="space-y-3">
                    @for (prompt of activePrompts(); track prompt.pid) {
                    <div
                        class="rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-700/30 overflow-hidden transition-colors">
                        <div class="px-4 py-3 flex items-start justify-between gap-4">
                            <div class="flex-1 min-w-0">
                                <div class="flex items-center gap-2 mb-1">
                                    @if (prompt.label) {
                                    <span
                                        class="inline-flex items-center px-2 py-0.5 rounded-md bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs font-medium">
                                        {{ prompt.label }}
                                    </span>
                                    }
                                    <span class="text-xs text-gray-400 dark:text-gray-500">ID: {{ prompt.pid }}</span>
                                </div>
                                <p
                                    class="text-sm text-gray-700 dark:text-gray-200 line-clamp-3 whitespace-pre-wrap break-words">
                                    {{ prompt.prompt }}
                                </p>
                                <p class="text-xs text-gray-400 dark:text-gray-500 mt-2">
                                    Updated: {{ prompt.updatedAt | date:'medium' }}
                                </p>
                            </div>
                            <div class="flex items-center gap-1 shrink-0">
                                <button type="button" (click)="openEditPromptDialog(prompt)"
                                    class="flex items-center justify-center p-1.5 rounded-md text-gray-400 hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-white/10 dark:hover:text-white focus:outline-none transition-colors"
                                    title="Edit prompt">
                                    <app-icon-v2 name="pencil" [size]="16" />
                                </button>
                                <button type="button" (click)="openConfirmDeletePrompt(prompt)"
                                    class="flex items-center justify-center p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 dark:hover:text-red-400 focus:outline-none transition-colors"
                                    title="Delete prompt">
                                    <app-icon-v2 name="trash" [size]="16" />
                                </button>
                            </div>
                        </div>
                    </div>
                    } @empty {
                    <div
                        class="text-center text-lg text-gray-500 dark:text-gray-400 bg-gray-50/50 dark:bg-gray-700/30 rounded-lg px-4 py-8 border border-gray-200 dark:border-gray-600">
                        No prompts configured, add one above to get started.
                    </div>
                    }
                </div>

                @if (deletedPrompts().length > 0) {
                <div class="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <h3 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Deleted Prompts ({{
                        deletedPrompts().length }})</h3>
                    <div class="space-y-2">
                        @for (prompt of deletedPrompts(); track prompt.pid) {
                        <div
                            class="rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-100/50 dark:bg-gray-800/30 overflow-hidden opacity-60 hover:opacity-100 transition-opacity">
                            <div class="px-4 py-3 flex items-start justify-between gap-4">
                                <div class="flex-1 min-w-0">
                                    <div class="flex items-center gap-2 mb-1">
                                        @if (prompt.label) {
                                        <span
                                            class="inline-flex items-center px-2 py-0.5 rounded-md bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-xs font-medium">
                                            {{ prompt.label }}
                                        </span>
                                        }
                                        <span class="text-xs text-gray-400 dark:text-gray-500">ID: {{ prompt.pid
                                            }}</span>
                                        <span
                                            class="inline-flex items-center px-2 py-0.5 rounded-md bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-xs font-medium">
                                            Deleted
                                        </span>
                                    </div>
                                    <p
                                        class="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 whitespace-pre-wrap break-words">
                                        {{ prompt.prompt }}
                                    </p>
                                </div>
                                <button type="button" (click)="restorePrompt(prompt)"
                                    class="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600/50 focus:outline-none transition-colors shrink-0"
                                    title="Restore prompt">
                                    <app-icon-v2 name="RotateCcw" [size]="14" />
                                    Restore
                                </button>
                            </div>
                        </div>
                        }
                    </div>
                </div>
                }
                }
            </div>
        </div>
    </div>
    }
    @case ('ai_providers') {
    <div class="mt-6 space-y-4">
        <div
            class="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/80 shadow-sm overflow-hidden card-classes">
            <div class="px-5 py-4 border-b border-gray-100 dark:border-gray-700/80">
                <h2 class="text-base font-semibold text-gray-900 dark:text-white">AI Providers</h2>
                <p class="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Manage providers and their models.</p>
            </div>
            <div class="p-5 space-y-4">
                <!-- Add provider form -->
                <div
                    class="rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-700/20 p-4 space-y-3">
                    <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Add provider</span>
                    <div class="flex flex-wrap items-end gap-3">
                        <div class="min-w-[120px]">
                            <label class="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Key name
                                *</label>
                            <input type="text" [ngModel]="newKeyname()" (ngModelChange)="newKeyname.set($event)"
                                placeholder="e.g. openai, gemini"
                                class="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700/50 text-gray-900 dark:text-gray-100 text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-colors" />
                        </div>
                        <div class="min-w-[140px]">
                            <label class="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Display name
                                *</label>
                            <input type="text" [ngModel]="newName()" (ngModelChange)="newName.set($event)"
                                placeholder="e.g. OpenAI" maxlength="255"
                                class="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700/50 text-gray-900 dark:text-gray-100 text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-colors" />
                        </div>
                        <div class="min-w-[400px]">
                            <label class="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">API key
                                (optional)</label>
                            <input type="password" [ngModel]="newApiKeyEnc()" (ngModelChange)="newApiKeyEnc.set($event)"
                                placeholder="Encrypted on save" autocomplete="off"
                                class="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700/50 text-gray-900 dark:text-gray-100 text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-colors" />
                        </div>
                        <button type="button" (click)="addProviderFromForm()"
                            [disabled]="!newKeyname().trim() || !newName().trim()"
                            class="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-purple-500 hover:bg-purple-600 disabled:opacity-50 disabled:pointer-events-none text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 transition-colors">
                            <app-icon-v2 name="plus" [size]="14" />
                            Add
                        </button>
                    </div>
                </div>

                <div class="my-4">
                    <h3 class="text-sm font-medium text-gray-700 dark:text-gray-300">Total Providers: {{
                        providers().length || 0 }}</h3>
                </div>

                <!-- Provider list -->
                <div class="space-y-2">
                    @for (provider of providers(); track provider.pid) {
                    <div
                        class="rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-700/30 overflow-hidden transition-colors">
                        <button type="button" (click)="toggleProvider(provider.pid)"
                            class="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-100/80 dark:hover:bg-gray-600/30 transition-colors">
                            <div class="flex items-center gap-3">
                                <app-icon-v2 [name]="isExpanded(provider.pid) ? 'chevronDown' : 'chevronRight'"
                                    [size]="18" class="text-gray-500 dark:text-gray-400 shrink-0" />
                                <div class="flex flex-col gap-0.5">
                                    <div class="flex items-center gap-2">
                                        <span class="text-sm font-semibold text-gray-900 dark:text-white">{{
                                            provider.name }}</span>
                                        <span class="text-xs text-gray-500 dark:text-gray-400">({{ provider.modelCount
                                            }}
                                            model{{ provider.modelCount !== 1 ? 's' : '' }})</span>
                                    </div>
                                    <span class="text-xs text-gray-400 dark:text-gray-500">{{ provider.keyname }}</span>
                                </div>
                            </div>
                            <button type="button" (click)="openConfirmDeleteProvider(provider.pid, $event)"
                                class="flex items-center justify-center p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 dark:hover:text-red-400 focus:outline-none transition-colors"
                                title="Remove provider">
                                <app-icon-v2 name="trash" [size]="16" />
                            </button>
                        </button>
                        @if (isExpanded(provider.pid)) {
                        <div class="px-4 pb-4 pt-1 border-t border-gray-200/80 dark:border-gray-600/80 space-y-3">
                            @if (modelsLoading() === provider.pid) {
                            <div class="flex items-center gap-2 py-2 text-sm text-gray-500 dark:text-gray-400">
                                <app-icon-v2 name="LoaderCircle" [size]="16" customClass="animate-spin" />
                                <span>Loading models…</span>
                            </div>
                            } @else {
                            <!-- Models list -->
                            @if (getModelsForProvider(provider.pid).length > 0) {
                            <ul class="flex flex-wrap gap-2 mt-2">
                                @for (m of getModelsForProvider(provider.pid); track m.pid) {
                                <li class="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-white dark:bg-gray-700/60 border border-gray-200 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-200"
                                    [title]="(m.description ? m.description + '\n' : '') + 'model: ' + m.model + '\nCreated: ' + m.createdAt + '\nUpdated: ' + m.updatedAt">
                                    <span>{{ m.model }}</span>
                                    <button type="button" (click)="openConfirmDeleteModel(provider.pid, m)"
                                        class="p-0.5 rounded text-gray-400 hover:text-red-600 dark:hover:text-red-400 focus:outline-none transition-colors flex items-center justify-center"
                                        title="Remove model">
                                        <app-icon-v2 name="x" [size]="14" />
                                    </button>
                                </li>
                                }
                            </ul>
                            } @else {
                            <p class="text-sm text-gray-400 dark:text-gray-500 py-1">No models configured yet.</p>
                            }
                            <!-- Add model button -->
                            <button type="button" (click)="openAddModelDialog(provider.pid)"
                                class="inline-flex items-center gap-1.5 px-2.5 py-2 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600/50 focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-colors">
                                <app-icon-v2 name="plus" [size]="14" />
                                Add model
                            </button>
                            }
                        </div>
                        }
                    </div>
                    } @empty {
                    <div
                        class="text-center text-lg text-gray-500 dark:text-gray-400 bg-gray-50/50 dark:bg-gray-700/30 rounded-lg px-4 py-8 border border-gray-200 dark:border-gray-600">
                        No provider configured, add one above to get started.
                    </div>
                    }
                </div>
            </div>
        </div>
    </div>
    }
    }
</div>

<!-- Add Model dialog -->
@if (addModelDialogOpen()) {
<div class="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm shadow-2xl"
    (click)="closeAddModelDialog()">
    <div class="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl w-full max-w-md overflow-hidden card-classes"
        (click)="$event.stopPropagation()">
        <div class="px-5 py-4 border-b border-gray-100 dark:border-gray-700/80 flex items-center justify-between">
            <h3 class="text-base font-semibold text-gray-900 dark:text-white">Add model</h3>
            <button type="button" (click)="closeAddModelDialog()"
                class="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none transition-colors">
                <app-icon-v2 name="x" [size]="20" />
            </button>
        </div>
        <form (ngSubmit)="submitAddModel()" class="p-5 space-y-4">
            <p class="text-xs text-gray-500 dark:text-gray-400">Provider: {{ addModelDialogProviderName() }}</p>
            <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Model *</label>
                <input type="text" [ngModel]="newModelModel()" (ngModelChange)="newModelModel.set($event)" name="model"
                    placeholder="e.g. gpt-4o" maxlength="255"
                    class="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700/50 text-gray-900 dark:text-gray-100 text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-colors" />
            </div>
            <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Description
                    (optional)</label>
                <app-textarea [ngModel]="newModelDescription()" (ngModelChange)="newModelDescription.set($event)"
                    name="description" placeholder="Optional description" [rows]="3" />
            </div>
            <div class="flex items-center justify-end gap-3 pt-2">
                <app-button type="button" variant="outline" (buttonClick)="closeAddModelDialog()">Cancel</app-button>
                <app-button type="submit" variant="primary" (buttonClick)="submitAddModel()"
                    [disabled]="!newModelModel().trim()">
                    Add model
                </app-button>
            </div>
        </form>
    </div>
</div>
}

<!-- Edit Prompt dialog -->
@if (editPromptDialogOpen()) {
<div class="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm shadow-2xl"
    (click)="closeEditPromptDialog()">
    <div class="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl w-full max-w-2xl overflow-hidden card-classes"
        (click)="$event.stopPropagation()">
        <div class="px-5 py-4 border-b border-gray-100 dark:border-gray-700/80 flex items-center justify-between">
            <h3 class="text-base font-semibold text-gray-900 dark:text-white">Edit prompt</h3>
            <button type="button" (click)="closeEditPromptDialog()"
                class="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none transition-colors">
                <app-icon-v2 name="x" [size]="20" />
            </button>
        </div>
        <form (ngSubmit)="submitEditPrompt()" class="p-5 space-y-4">
            <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Label *</label>
                <input type="text" [ngModel]="editPromptLabel()" (ngModelChange)="editPromptLabel.set($event)"
                    (blur)="markEditPromptTouched('label')"
                    name="label" placeholder="e.g. customer-support" maxlength="255"
                    [class.border-red-500]="editPromptTouched()['label'] && editPromptErrors().label"
                    class="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700/50 text-gray-900 dark:text-gray-100 text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-colors" />
                @if (editPromptTouched()['label'] && editPromptErrors().label) {
                <p class="text-[10px] text-red-500 mt-1 font-medium">{{ editPromptErrors().label }}</p>
                }
            </div>
            <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Prompt *</label>
                <app-textarea [ngModel]="editPromptText()" (ngModelChange)="editPromptText.set($event)" name="prompt"
                    (inputBlur)="markEditPromptTouched('prompt')"
                    placeholder="Enter the system prompt text..." [rows]="8"
                    [error]="editPromptTouched()['prompt'] ? (editPromptErrors().prompt || '') : ''" />
            </div>
            <div class="flex items-center justify-end gap-3 pt-2">
                <app-button type="button" variant="outline" (buttonClick)="closeEditPromptDialog()">Cancel</app-button>
                <app-button type="submit" variant="primary" (buttonClick)="submitEditPrompt()">
                    Save changes
                </app-button>
            </div>
        </form>
    </div>
</div>
}

<!-- Delete confirmation dialog -->
@if (confirmDeleteType()) {
<div class="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm shadow-2xl"
    (click)="closeConfirmDelete()">
    <div class="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl w-full max-w-md overflow-hidden card-classes"
        (click)="$event.stopPropagation()">
        <div class="px-5 py-4 border-b border-gray-100 dark:border-gray-700/80 flex items-center justify-between">
            <h3 class="text-base font-semibold text-gray-900 dark:text-white">Confirm delete</h3>
            <button type="button" (click)="closeConfirmDelete()"
                class="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none transition-colors">
                <app-icon-v2 name="x" [size]="20" />
            </button>
        </div>
        <div class="p-5 space-y-4">
            <p class="text-sm text-gray-700 dark:text-gray-200">{{ confirmDeleteMessage() }}</p>
            <div class="flex items-center justify-end gap-3 pt-2">
                <app-button type="button" variant="outline" (buttonClick)="closeConfirmDelete()">Cancel</app-button>
                <app-button type="button" variant="danger" (buttonClick)="confirmDelete()">Confirm</app-button>
            </div>
        </div>
    </div>
</div>
}